// src/services/adaptiveVideo.js — playing the ladder the server already built.
//
// ══ 🟥 THE BUG THIS FIXES ═══════════════════════════════════════════════════
// Every video posted to Allo is transcoded into a four-rung HLS ladder —
// 240p/480p/720p/1080p, each with a declared peak and average bandwidth — and
// the browser has never played a single rung of it. The web read `videoUrl` and
// streamed the ORIGINAL upload: a 60MB phone recording, at full resolution, over
// whatever connection the reader happened to have, with no way to step down.
// The adaptive feature the app has had all along simply did not exist here.
//
// ══ 🟥 AND `<video src=".m3u8">` IS NOT THE ANSWER ═════════════════════════
// Safari plays HLS natively. Chrome, Firefox and Edge do not, and they fail
// SILENTLY — a black rectangle and no error anybody sees. So: native where it
// exists (it is better, it is hardware-accelerated, and it costs no download),
// hls.js everywhere else, and the plain mp4 as the last resort so a browser
// that can do neither still plays the video.
//
// 🟢 THE STARTING RUNG IS DELIBERATELY LOW. hls.js defaults to picking a level
//    from an estimate it does not have yet on the first video of a session; on
//    a phone connection that means the first reel opens at 1080p, buffers for
//    four seconds, and the reader is gone before it plays. Starting at the
//    second rung and letting the estimator climb is the difference between
//    "instant" and "why is this slow".

let Hls = null;
let loading = null;

/* 🟢 LOADED ON DEMAND, NOT AT STARTUP. hls.js is ~150KB of parser that a Safari
   reader never needs and a reader who opens no video never needs at all —
   putting it in the main bundle would slow the first paint of the whole app to
   make one screen slightly simpler. */
async function ensureHls() {
  if (Hls) return Hls;
  if (!loading) loading = import('hls.js').then((m) => { Hls = m.default || m; return Hls; }).catch(() => null);
  return loading;
}

/** Does this browser play HLS by itself? (Safari, iOS, some smart TVs.) */
export function nativeHls(video) {
  try {
    return !!video?.canPlayType && video.canPlayType('application/vnd.apple.mpegurl') !== '';
  } catch {
    return false;
  }
}

/** The ladder, if the transcoder has finished building one. */
export const hlsUrlOf = (post) => (post && typeof post.hlsUrl === 'string' && post.hlsUrl) || null;

/**
 * 🎞️ The ladder for ONE clip of a post. Every video slide is encoded now and
 * carries its own `media[i].hlsUrl`; the post-level `hlsUrl` belongs to the first
 * video only (the one matching `videoUrl`, else the first video). Without a file
 * url to match, the post's own ladder is used, as before.
 * 🟥 Slides 2+ used to be handed the FIRST video's ladder — a different clip.
 */
export function hlsUrlFor(post, fileUrl) {
  if (!post) return null;
  const media = Array.isArray(post.media) ? post.media : [];
  if (!fileUrl) return hlsUrlOf(post);
  const m = media.find((x) => x && x.type === 'video' && x.url === fileUrl);
  if (m && typeof m.hlsUrl === 'string' && m.hlsUrl) return m.hlsUrl;
  const first = media.find((x) => x && x.type === 'video' && post.videoUrl && x.url === post.videoUrl)
    || media.find((x) => x && x.type === 'video');
  if (!m || !first || first === m || fileUrl === post.videoUrl) return hlsUrlOf(post);
  return null;
}

/**
 * Attach adaptive playback to a <video>.
 *
 * @param video   the element
 * @param post    the post (for `hlsUrl`)
 * @param fallback the plain file url, used when there is no ladder yet
 * @return a teardown function. ALWAYS call it: an hls.js instance holds a
 *         MediaSource and several buffers, and a reel feed that leaks one per
 *         swipe runs a phone out of memory in about two minutes.
 *
 * 🟥 THE FALLBACK IS NOT AN ERROR PATH, IT IS THE NORMAL ONE FOR A NEW POST.
 *    Transcoding takes a minute or two; a video watched in that window has no
 *    ladder, and it has to play. So the mp4 is set first and the ladder swapped
 *    in only when it exists — which also means a failure at any point below
 *    leaves a working video rather than a black box.
 */
/* 📶 the bandwidth hls.js last measured, carried to the next player (bits/s) */
export const DEFAULT_START_BPS = 3000000;
let _lastBps = 0;
export function rememberBandwidth(bps) {
  const n = Number(bps);
  if (Number.isFinite(n) && n > 50000) _lastBps = n;
}
export function lastBandwidth() { return _lastBps > 0 ? _lastBps : DEFAULT_START_BPS; }

export function attachAdaptive(video, post, fallback) {
  if (!video) return () => {};
  const ladder = hlsUrlFor(post, fallback);

  if (!ladder) {
    if (fallback && video.src !== fallback) video.src = fallback;
    return () => {};
  }

  if (nativeHls(video)) {
    /* Safari: hand it the playlist and get out of the way. Its own ABR is
       better than anything a script can do from outside the pipeline. */
    video.src = ladder;
    return () => {};
  }

  let hls = null;
  let dead = false;

  (async () => {
    const H = await ensureHls();
    if (dead) return;
    if (!H || !H.isSupported()) {
      /* an old browser with no MediaSource — the mp4 still plays */
      if (fallback) video.src = fallback;
      return;
    }
    hls = new H({
      /* 🟥 EVERY REEL STARTED ON THE SAME LOW RUNG. `startLevel: 1` with a fresh
         800 kbps guess per player meant each swipe opened blurry and spent its
         first seconds climbing — on a connection already measured a reel ago.
         🟢 Auto start (-1) from the LAST measured bandwidth (kept across players,
         ~3 Mbps before the first measurement), so a good connection opens sharp. */
      startLevel: -1,
      /* a reel is short; a deep forward buffer is wasted download on a clip
         somebody is about to swipe past */
      maxBufferLength: 12,
      maxMaxBufferLength: 30,
      backBufferLength: 10,
      /* the ladder declares AVERAGE-BANDWIDTH as well as peak, which is what
         makes a sensible choice possible on a variable-bitrate encode */
      abrEwmaDefaultEstimate: lastBandwidth(),
      lowLatencyMode: false,
      enableWorker: true,
    });
    hls.on(H.Events.ERROR, (_e, data) => {
      if (!data?.fatal) return;
      /* 🟥 A FATAL ERROR RECOVERS ONCE, THEN GIVES THE MP4 BACK. Retrying a
         broken manifest for ever is how a video ends up as a permanent black
         rectangle; the original file is right there and works. */
      if (data.type === H.ErrorTypes.NETWORK_ERROR) { try { hls.startLoad(); return; } catch { /* fall through */ } }
      if (data.type === H.ErrorTypes.MEDIA_ERROR) { try { hls.recoverMediaError(); return; } catch { /* fall through */ } }
      try { hls.destroy(); } catch { /* already gone */ }
      hls = null;
      if (fallback) { video.src = fallback; video.load(); }
    });
    hls.loadSource(ladder);
    hls.attachMedia(video);
  })();

  return () => {
    dead = true;
    if (hls) { rememberBandwidth(hls.bandwidthEstimate); try { hls.destroy(); } catch { /* already gone */ } hls = null; }
  };
}
